import { useMutation, useQuery, useQueryClient } from "react-query";
import { queryClient } from "~/repository/index";
import Angie from "~/utils/angie";
import { fetch } from "~/utils/fetch";
import { invalidateProjectCounters } from "./projectUtils";

const DEFAULT_STALE = 5000;
export const useNotebooks = (projectId) =>
  useQuery({
    queryKey: ["notebooks", projectId],
    queryFn: ({ signal }) => {
      return fetch({
        url: `${Angie.api_url}/projects/${projectId}/notebooks/for-screen`,
        signal,
      });
    },
    staleTime: DEFAULT_STALE,
  });

export const prefetchNotebooks = (projectId) => {
  return queryClient.fetchQuery({
    queryKey: ["notebooks", projectId],
    queryFn: ({ signal }) => {
      return fetch({
        url: `${Angie.api_url}/projects/${projectId}/notebooks/for-screen`,
        signal,
      });
    },
    staleTime: DEFAULT_STALE,
  });
};

export const useNotebook = (projectId, notebookId, config) =>
  useQuery({
    ...config,
    queryKey: ["notebook", notebookId],
    enabled: notebookId > 0,
    queryFn: ({ signal }) => {
      return fetch({
        url: `${Angie.api_url}/projects/${projectId}/notebooks/${notebookId}`,
        signal,
      });
    },
  });

export const useCreateNotebookMutation = (projectId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => {
      return fetch({
        url: `${Angie.api_url}/projects/${projectId}/notebooks`,
        method: "POST",
        body: data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["notebooks", projectId]);
      invalidateProjectCounters(projectId);
    },
  });
};

export const useUpdateNotebookMutation = (projectId, id) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => {
      return fetch({
        url: `${Angie.api_url}/projects/${projectId}/notebooks/${id}`,
        method: "PUT",
        body: data,
      });
    },
    onSuccess: (response) => {
      queryClient.setQueryData(["notebook", id], response);
      queryClient.invalidateQueries(["notebooks", projectId]);
    },
  });
};

export const useMoveCopyToProjectNotebookMutation = (projectId, id) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => {
      return fetch({
        url: `${Angie.api_url}/projects/${projectId}/notebooks/${id}/move-to-project`,
        method: "PUT",
        body: data,
      });
    },
    onSuccess: (response) => {
      queryClient.setQueryData(["notebook", id], response);
      queryClient.invalidateQueries(["notebooks", projectId]);
      invalidateProjectCounters(projectId);
    },
  });
};

export const useMoveToTrashNotebookMutation = (projectId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => {
      return fetch({
        url: `${Angie.api_url}/move-to-trash/notebook/${id}`,
        method: "PUT",
      });
    },
    onSuccess: (response) => {
      queryClient.setQueryData(["notebook", response.single.id], response);
      queryClient.invalidateQueries(["notebooks", projectId]);
      invalidateProjectCounters(projectId);
    },
  });
};

export const useRestoreFromTrashNotebookMutation = (projectId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => {
      return fetch({
        url: `${Angie.api_url}/restore-from-trash/notebook/${id}`,
        method: "PUT",
      });
    },
    onSuccess: (response) => {
      queryClient.setQueryData(["notebook", response.single.id], response);
      queryClient.invalidateQueries(["notebooks", projectId]);
      invalidateProjectCounters(projectId);
    },
  });
};
